import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ClientLoggerService } from './client-logger.service';
import { LogCategory } from './client-log.models';
import { UpdateService } from '../pwa/update.service';
import { isNetworkFailure } from './network-failure';

/**
 * Globaler Error Handler für die gesamte Angular-Anwendung
 * Fängt alle unbehandelten Errors und loggt sie automatisch
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {}

  handleError(error: Error | HttpErrorResponse): void {
    // Lazy-Loading des ClientLoggerService um zirkuläre Abhängigkeiten zu vermeiden
    const logger = this.injector.get(ClientLoggerService);

    // Prüfe auf Lazy-Loading-Fehler (ChunkLoadError oder Failed to fetch dynamically imported module)
    if (this.isChunkLoadError(error)) {
      this.handleChunkLoadError(error, logger);
      return;
    }

    if (error instanceof HttpErrorResponse) {
      // HTTP-Fehler (z.B. 404, 500, Network-Errors; Status 0 loggt logHttpError als Warning)
      this.handleHttpError(error, logger);
    } else if (isNetworkFailure(error)) {
      // Roher fetch-Abbruch (kein Netz beim Kunden) ist kein Systemfehler
      logger.logWarning(`Network Error: ${error.message}`, LogCategory.Network, {
        componentName: 'GlobalErrorHandler',
        networkFailure: true,
      });
    } else {
      // Client-Side Fehler (z.B. JavaScript Errors)
      this.handleClientError(error, logger);
    }

    // Fehler weiterhin in Console ausgeben für Entwickler
    console.error('Caught by GlobalErrorHandler:', error);
  }

  /**
   * Behandelt HTTP-Fehler
   */
  private handleHttpError(error: HttpErrorResponse, logger: ClientLoggerService): void {
    let errorMessage: string;
    let category = LogCategory.Network;

    if (error.error instanceof ErrorEvent) {
      // Client-Side Network Error (z.B. keine Verbindung)
      errorMessage = `Network Error: ${error.error.message}`;
    } else {
      // Server-Side HTTP Error
      errorMessage = `HTTP ${error.status}: ${error.message}`;

      // Kategorie basierend auf Status-Code
      if (error.status === 401 || error.status === 403) {
        category = LogCategory.Security;
      }
    }

    logger.logHttpError(
      error.url ? this.extractHttpMethod(error) : 'UNKNOWN',
      error.url || 'unknown',
      error.status,
      errorMessage
    );
  }

  /**
   * Behandelt Client-Side JavaScript Errors
   */
  private handleClientError(error: Error, logger: ClientLoggerService): void {
    // Versuche Component-Name aus Stack-Trace zu extrahieren
    const componentName = this.extractComponentName(error);

    logger.logError(error, {
      componentName,
      category: LogCategory.Error,
      properties: {
        errorName: error.name,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Extrahiert Component-Name aus Error-Stack
   */
  private extractComponentName(error: Error): string | undefined {
    if (!error.stack) return undefined;

    // Suche nach Angular-Component-Pattern
    const match = error.stack.match(/at (\w+Component)/);
    return match ? match[1] : undefined;
  }

  /**
   * Versucht HTTP-Methode aus Error zu extrahieren
   */
  private extractHttpMethod(error: HttpErrorResponse): string {
    // Fallback auf GET wenn nicht ermittelbar
    return 'GET';
  }

  /**
   * Prüft ob es sich um einen Lazy-Loading-Fehler handelt
   */
  private isChunkLoadError(error: any): boolean {
    const errorMessage = error?.message || error?.toString() || '';
    return (
      error?.name === 'ChunkLoadError' ||
      errorMessage.includes('Failed to fetch dynamically imported module') ||
      errorMessage.includes('Loading chunk') ||
      errorMessage.includes('ChunkLoadError')
    );
  }

  /**
   * Behandelt Lazy-Loading-Fehler (ChunkLoadError)
   * Dies tritt auf, wenn ein neues Deployment erfolgt ist und alte Chunk-Dateien nicht mehr verfügbar sind
   */
  private handleChunkLoadError(error: Error, logger: ClientLoggerService): void {
    const errorMessage = `Lazy-Loading-Fehler: ${error.message}. Eine neue Version wurde deployed.`;

    logger.logWarning(errorMessage, LogCategory.Performance, {
      componentName: 'GlobalErrorHandler',
      errorType: 'ChunkLoadError',
      errorMessage: error.message,
      errorName: error.name,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    // Verwende UpdateService für benutzerfreundliches Reload-Handling
    // Der UpdateService prüft auf ungespeicherte Änderungen und zeigt eine Benachrichtigung
    try {
      const updateService = this.injector.get(UpdateService);
      updateService.forceReload();
    } catch (err) {
      // Fallback: Direktes Reload wenn UpdateService nicht verfügbar ist
      console.warn('UpdateService nicht verfügbar, führe direktes Reload durch');
      window.location.reload();
    }
  }
}
